import React, { useState } from "react";
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  TextInput,
  PasswordInput,
  Alert,
} from "@mantine/core";
import { IconKey, IconAlertCircle } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getAdminApiEndpoint } from "../../config/config";
import { adminHeaders } from "../../services/adminApi";
import { handleGenericError } from "../../utils/errorHandler";

function ResetPasswordModal({ isOpen, onClose, companyCode }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getAdminApiEndpoint("/v2/admin/reset-password"),
        {
          method: "POST",
          headers: adminHeaders(jwt, companyCode),
          body: JSON.stringify({
            email,
            password,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to reset password: ${response.status}`
        );
      }

      showSuccess("Password reset successfully");
      handleClose();
    } catch (error) {
      handleGenericError(error, "Failed to reset password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setErrors({});
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconKey size={20} />
          <Text fw={600} size="lg">
            Reset User Password
          </Text>
        </Group>
      }
      size="md"
      centered
    >
      <Stack gap="md">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="blue"
          variant="light"
        >
          <Text size="sm">
            This will reset the password for a user in this client organization.
            Enter the user's email address and their new password.
          </Text>
        </Alert>

        <TextInput
          label="User Email"
          placeholder="user@example.com"
          value={email}
          onChange={event => setEmail(event.currentTarget.value)}
          error={errors.email}
          required
          disabled={isSubmitting}
        />

        <PasswordInput
          label="New Password"
          placeholder="Enter new password"
          value={password}
          onChange={event => setPassword(event.currentTarget.value)}
          error={errors.password}
          required
          disabled={isSubmitting}
        />

        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={event => setConfirmPassword(event.currentTarget.value)}
          error={errors.confirmPassword}
          required
          disabled={isSubmitting}
        />

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            leftSection={<IconKey size={16} />}
            onClick={handleSubmit}
            loading={isSubmitting}
            color="red"
          >
            Reset Password
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default ResetPasswordModal;
